// ClockTile.jsx
//
// Large iPhone-style clock — shown in the dashboard's live-stream tile slot
// for devices WITHOUT the VIP addon (VIP devices get the live stream instead).
//
// TIMEZONE: uses the `timezone` prop, which is the customer's saved account
// timezone (from cf_select_your_time_zone via the sub_info_update socket in
// App.js — already normalized to a moment tz string like "EST5EDT"). It does
// NOT use the Android device's system timezone, which is unreliable. If the
// customer's timezone changes in the database, the socket pushes the new
// value down and the next 1-second tick renders in the new zone.
//
// ─── Props ────────────────────────────────────────────────────────────────
//   timezone  customer's normalized tz string (e.g. "EST5EDT", "PST8PDT").
//   style     (optional) merged onto the outer tile container.

import { useEffect, useState } from "react";
import moment from "moment-timezone";

const ClockTile = ({ timezone, style }) => {
  // Tick once per second. We store a counter, not the formatted string, so
  // the time is always re-derived from the CURRENT timezone prop each render.
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Derive time from the customer's timezone. If no timezone yet, fall back
  // to local just so the tile isn't blank (rare — only before socket data).
  const now = timezone ? moment().tz(timezone) : moment();
  const timeStr = now.format("h:mm"); // 12-hour, no leading zero (e.g. 9:41)
  const ampm = now.format("A"); // AM / PM

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        borderRadius: 20,
        overflow: "hidden",
        background: "#0b1f4d",
        // White rounded border — matches the live stream tile's frame.
        border: "3px solid #ffffff",
        boxShadow: "0 10px 28px rgba(11,31,53,0.16)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Lato', system-ui, -apple-system, sans-serif",
        ...style,
      }}
    >
      {/* Time — large white numerals, iPhone style. AM/PM smaller, gold. */}
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: "0.18em",
          lineHeight: 1,
        }}
      >
        <span
          style={{
            fontSize: "clamp(60px, 22vh, 150px)",
            fontWeight: 800,
            color: "#ffffff",
            letterSpacing: "-0.02em",
          }}
        >
          {timeStr}
        </span>
        <span
          style={{
            fontSize: "clamp(20px, 7vh, 46px)",
            fontWeight: 800,
            color: "#c9a84c",
            letterSpacing: "0.02em",
          }}
        >
          {ampm}
        </span>
      </div>
    </div>
  );
};

export default ClockTile;
